import type { WorkflowContent } from "../../../api/content/authoring/processContentDecoders";
import type { ItemEdit } from "../../../api/content/changeSetsApi";
import { parseDocumentVersionRef } from "../../../shared/references/contentReferences";
import { referenceOf } from "./workflowDraft";

/**
 * The copies the step drawer made while a Workflow is edited, each by the exact version it was
 * copied from (WD-1+3 D-8b, built provisionally, awaiting acceptance). The draft pins a copy where
 * it runs the step itself; the Workflow's save carries each into the placements of its source
 * deeper in its tree, which module B moves and checks.
 */
export type CarriedCopies = ReadonlyMap<string, string>;

export const NO_CARRIED_COPIES: CarriedCopies = new Map();

/**
 * The copies after the drawer saved `edited` as `saved`: a carried copy saved again moves its
 * pair on; a save under a new item ID is a new copy of `edited`. The next version of the project's
 * own step is no copy: it records no source.
 */
export function withSavedStep(copies: CarriedCopies, edited: string, saved: string): CarriedCopies {
  const source = [...copies].find(([, copy]) => copy === edited)?.[0];
  if (source !== undefined) {
    return new Map(copies).set(source, saved);
  }
  return parseDocumentVersionRef(edited)?.documentId === parseDocumentVersionRef(saved)?.documentId ? copies : new Map(copies).set(edited, saved);
}

/** A draft's placements by key. */
export type Placements = Readonly<Record<string, string>>;

export function placementsOf(draft: WorkflowContent): Placements {
  // An abstract step pins nothing (ADR-0043 D1).
  return Object.fromEntries(draft.steps.flatMap((use) => {
    const reference = referenceOf(use);
    return reference === null ? [] : [[use.key, reference] as const];
  }));
}

/**
 * What an editing session carries into the Workflow's saves: the drawer's copies, and the draft's
 * placements as the last successful save sent them, or as opened. A placement changed since is
 * never continued in that save's copy (module B's rule).
 */
export interface CarrySession {
  readonly copies: CarriedCopies;
  readonly submitted: Placements;
}

export type CarrySessionEvent =
  | { readonly type: "stepSaved"; readonly edited: string; readonly saved: string }
  | { readonly type: "workflowSaved"; readonly submitted: Placements }
  | { readonly type: "discarded"; readonly opened: WorkflowContent };

export function openedSession(opened: WorkflowContent): CarrySession {
  return { copies: NO_CARRIED_COPIES, submitted: placementsOf(opened) };
}

/** The session after `event`. Discard starts it again as opened: the draft is the opened one, so
 * nothing a discarded save made is continued (Sol 4b r5 S5). */
export function carrySession(session: CarrySession, event: CarrySessionEvent): CarrySession {
  switch (event.type) {
    case "stepSaved":
      return { ...session, copies: withSavedStep(session.copies, event.edited, event.saved) };
    case "workflowSaved":
      return { ...session, submitted: event.submitted };
    case "discarded":
      return openedSession(event.opened);
  }
}

/** The Workflow's own edit with the session's copies to carry; the edit itself where there are none. */
export function carrying(edit: ItemEdit, session: CarrySession): ItemEdit {
  if (session.copies.size === 0 || (edit.operation !== "replace" && edit.operation !== "derive")) {
    return edit;
  }
  return { operation: "carry", edit, copies: [...session.copies].map(([source, version]) => ({ source, version })), submitted: { ...session.submitted } };
}
